<script setup lang="ts">
import PortalTopNav from '@/components/portal/TopNav.vue';
import { useAccessGate } from '@/composables/useAccessGate';
import { useRedirectAfterAuth } from '@/composables/useRedirectAfterAuth';
import type { AccessLevel } from '@/composables/useAccessLevel';
import { stripLocalePrefix } from '~/utils/i18n/locale';

const { t } = useI18n();

const { level } = defineProps<{
  level: AccessLevel;
}>();

const emit = defineEmits<{
  (e: 'sign-in'): void;
  (e: 'sign-up'): void;
  (e: 'verify-click'): void;
}>();

// Mirror the new portal nav for guests. Surfaces that are SEO-worthy
// (Projects, Developers, Vault, Services, News) are publicly reachable
// — guests browse the preview content. Locked items (Deals) require
// signup. Account/agent-only surfaces (Dashboard, Profile, Tiers,
// Earnings) live behind the avatar in the portal version and aren't
// shown to guests at all.
type GuestNavItem = {
  key: string;
  label: string;
  to: string;
  /** Public links navigate; locked links open the generic signup gate. */
  locked: boolean;
};

const route = useRoute();

const guestTabs = computed<GuestNavItem[]>(() => [
  { key: 'projects', label: t('nav.items.projects'), to: '/explore', locked: false },
  { key: 'developers', label: t('nav.items.developers'), to: '/developers', locked: false },
  { key: 'vault', label: t('nav.items.vault'), to: '/developer-vault', locked: false },
  { key: 'services', label: t('nav.items.services'), to: '/services', locked: false },
  { key: 'news', label: t('nav.items.news'), to: '/news', locked: true },
  { key: 'deals', label: t('nav.items.dealsShort'), to: '/deals', locked: true },
]);

const isActive = (tab: GuestNavItem): boolean => {
  const p = stripLocalePrefix(route.path || '');
  if (tab.key === 'projects')
    return p.startsWith('/explore') || p.startsWith('/projects') || p.startsWith('/properties') || p.startsWith('/p/');
  if (tab.key === 'developers') return p.startsWith('/developers');
  if (tab.key === 'vault') return p.startsWith('/developer-vault') || p.startsWith('/vault');
  if (tab.key === 'services') return p.startsWith('/services');
  if (tab.key === 'news') return p.startsWith('/news');
  if (tab.key === 'deals') return p.startsWith('/deals');
  return false;
};

const { gate } = useAccessGate();
const { buildRedirectUrl } = useRedirectAfterAuth();

const onLockedNavClick = (event: MouseEvent) => {
  event.preventDefault();
  gate('generic', () => navigateTo(buildRedirectUrl('/auth/register')));
};
</script>

<template>
  <header
    v-if="level === 'guest'"
    data-guest-chrome
    class="sticky top-0 z-[100] flex h-[72px] items-center justify-between border-b border-black/[0.08] bg-white px-4 tablet:px-6 desktop:px-8"
  >
    <div class="flex items-center gap-3 desktop:gap-4">
      <UILogo
        :to="'/'"
        :size="28"
        variant="compact"
        :aria-label="t('property.access.accessTopNav.logoAriaLabel')"
      />
    </div>

    <nav
      class="absolute left-1/2 hidden -translate-x-1/2 desktop:flex"
      :aria-label="t('nav.aria.primary')"
    >
      <ul class="flex h-[72px] items-center gap-7">
        <li
          v-for="tab in guestTabs"
          :key="tab.key"
        >
          <NuxtLink
            :to="tab.to"
            class="group relative flex h-[72px] items-center gap-1.5 text-[14px] transition-colors duration-150"
            :class="[
              isActive(tab) ? 'font-semibold text-black-100' : 'font-medium text-black-60 hover:text-black-100',
              tab.locked ? 'cursor-pointer opacity-70 hover:opacity-100' : '',
            ]"
            :title="tab.locked ? t('property.access.accessTopNav.signUpToAccess') : undefined"
            :aria-disabled="tab.locked ? 'true' : undefined"
            @click="tab.locked ? onLockedNavClick($event) : undefined"
          >
            {{ tab.label }}
            <svg
              v-if="tab.locked"
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="text-black-60"
              aria-hidden="true"
            >
              <rect
                x="4"
                y="11"
                width="16"
                height="10"
                rx="2"
              />
              <path d="M8 11V8a4 4 0 1 1 8 0v3" />
            </svg>
            <span
              v-if="isActive(tab)"
              class="absolute inset-x-0 -bottom-px h-[2px] bg-black-100"
              aria-hidden="true"
            />
          </NuxtLink>
        </li>
      </ul>
    </nav>

    <div class="flex items-center gap-2">
      <!-- Below tablet the logo + Sign in + Sign up already fill a 375px header; the extra
           trigger would wrap both buttons, so the menu starts at tablet width here. -->
      <LocaleMenu class="hidden tablet:block" />
      <button
        type="button"
        class="inline-flex h-10 items-center rounded-[10px] px-3.5 text-[14px] font-semibold text-black-100 transition-colors hover:bg-black-03"
        @click="emit('sign-in')"
      >
        {{ t('property.shell.signIn') }}
      </button>
      <button
        type="button"
        class="inline-flex h-10 items-center rounded-[10px] bg-gold-primary-100 px-[18px] text-[14px] font-bold text-black-100 shadow-[0_4px_12px_rgba(255,182,72,0.35)] transition-colors hover:bg-gold-primary-120"
        @click="emit('sign-up')"
      >
        {{ t('property.shell.signUpFreeArrow') }}
      </button>
    </div>
  </header>

  <div
    v-else-if="level === 'registered'"
    class="relative"
  >
    <PortalTopNav />
    <div class="pointer-events-none absolute inset-y-0 end-[88px] hidden items-center desktop:flex">
      <button
        type="button"
        class="pointer-events-auto inline-flex h-8 items-center gap-1.5 rounded-full border border-success/30 bg-success/10 px-3 text-[12px] font-bold tracking-[0.01em] text-success transition-colors hover:bg-success/20"
        @click="emit('verify-click')"
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M12 3l8 3v6c0 5-3.4 8.5-8 9.5C7.4 20.5 4 17 4 12V6l8-3z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
        {{ t('property.access.accessTopNav.verifyPhone') }}
      </button>
    </div>
  </div>

  <PortalTopNav v-else />
</template>
